import React from "react";
import {
  Typography,
  Select,
  MenuItem,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Box,
} from "@mui/material";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";

export interface Condition {
  field: string;
  comparator: string;
  value: number;
}

const CONDITION_KEYS = [
  { label: "Temperature", key: "temperature", unit: "°C", showValue: true },
  { label: "Redox", key: "redox", unit: "mV", showValue: true },
  { label: "Time Elapsed", key: "timeElapsed", unit: "hours", showValue: true },
];

const getConditionDefn = (key: string) => {
  return CONDITION_KEYS.find((condition) => condition.key === key);
};

const COMPARATORS = [
  { label: "is above", value: ">" },
  { label: "is below", value: "<" },
];

export const ConditionInput = ({
  condition,
  removeable,
  readonly,
  onChange,
  onRemove,
}: {
  condition: Condition;
  removeable: boolean | null;
  readonly?: boolean;
  onChange?: (newCondition: Condition) => void;
  onRemove?: () => void;
}) => {
  const conditionDef = getConditionDefn(condition.field);

  const handleFieldChange = (field: string) => {
    onChange?.({ ...condition, field });
  };

  const handleComparatorChange = (comparator: string) => {
    onChange?.({ ...condition, comparator });
  };

  const handleValueChange = (value: string) => {
    onChange?.({ ...condition, value: parseFloat(value) });
  };

  return (
    // flex-wrap: on a phone the field + comparator + value selects do
    // not always fit on one row.
    <Box display="flex" alignItems="center" mb={1} gap={1} flexWrap="wrap">
      <FormControl size="small" style={{ minWidth: 120 }} disabled={readonly}>
        <InputLabel>Field</InputLabel>
        <Select
          label="Field"
          value={condition.field || ""}
          onChange={(e) => handleFieldChange(e.target.value as string)}
          disabled={readonly}
        >
          {CONDITION_KEYS.map((ck) => (
            <MenuItem key={ck.key} value={ck.key}>
              {ck.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {conditionDef?.showValue ? (
        <>
          <FormControl
            size="small"
            style={{ minWidth: 60 }}
            disabled={readonly}
          >
            <InputLabel>Comp.</InputLabel>
            <Select
              label="Comp."
              value={condition.comparator || ""}
              onChange={(e) => handleComparatorChange(e.target.value as string)}
              disabled={readonly}
            >
              {COMPARATORS.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            style={{ width: 100 }}
            size="small"
            label="Value"
            type="number"
            value={isFinite(condition.value) ? condition.value : ""}
            onChange={(e) => handleValueChange(e.target.value)}
            disabled={readonly}
          />
          <Typography>{conditionDef.unit}</Typography>
        </>
      ) : null}
      {removeable ? (
        <IconButton onClick={onRemove} size="small">
          <RemoveCircleIcon />
        </IconButton>
      ) : null}
    </Box>
  );
};
